import type { Breadcrumb, Event } from '@sentry/angular';

/**
 * Removes secrets from URLs before they reach Sentry: the tokens in password-reset, email
 * verification, supplier invitation and OTP links, and OAuth / token query values.
 * Used by Sentry.init beforeSend / beforeSendTransaction / beforeBreadcrumb in main.ts.
 */
const FILTERED = '[Filtered]';

/** Path segments that carry a secret: a prefix, then N secret segments. */
const SECRET_PATHS: { prefix: string; count: number }[] = [
    { prefix: '/password-reset/confirm/', count: 2 },  // :uid/:token
    { prefix: '/verify/', count: 2 },                  // :uid/:token (email verification)
    { prefix: '/register/supplier/', count: 1 },       // :token (page and API)
    { prefix: '/otp-static-url-link-verify/', count: 1 },
    { prefix: '/auth/otp/static/login/', count: 1 },
    { prefix: '/auth/otp/totp/login/', count: 1 },
];

/** Query (or fragment) parameter names whose values are secrets. Matched exactly. */
const SECRET_PARAMS = [
    'code', 'state', 't', 'ticket', 'token', 'access_token', 'refresh_token', 'id_token', 'refresh',
    'password', 'otp', 'uid',
];

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const PATH_RES = SECRET_PATHS.map(({ prefix, count }) => ({
    re: new RegExp(`(${escape(prefix)})${Array(count).fill('([^/?#\\s]+)').join('/')}`, 'g'),
    replacement: `$1${Array(count).fill(FILTERED).join('/')}`,
}));

const PARAM_RE = new RegExp(`([?#&](?:${SECRET_PARAMS.map(escape).join('|')})=)[^&#\\s]*`, 'g');
const BARE_PARAM_RE = new RegExp(`(^|&)((?:${SECRET_PARAMS.map(escape).join('|')})=)[^&#\\s]*`, 'g');

export function scrubUrl(url: string): string {
    if (typeof url !== 'string' || !url) {
        return url;
    }
    let out = url;
    for (const { re, replacement } of PATH_RES) {
        out = out.replace(re, replacement);
    }
    return out.replace(PARAM_RE, `$1${FILTERED}`);
}

/** A bare query string (no leading `?`), as Sentry stores it in request.query_string. */
function scrubQueryString(qs: unknown): unknown {
    if (typeof qs !== 'string') {
        return qs;
    }
    return scrubUrl(qs).replace(BARE_PARAM_RE, `$1$2${FILTERED}`);
}

function scrubData(data: Record<string, unknown> | undefined): void {
    if (!data) {
        return;
    }
    for (const key of ['url', 'from', 'to']) {
        if (typeof data[key] === 'string') {
            data[key] = scrubUrl(data[key] as string);
        }
    }
}

export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
    if (!breadcrumb) {
        return breadcrumb;
    }
    scrubData(breadcrumb.data as Record<string, unknown> | undefined);
    if (typeof breadcrumb.message === 'string') {
        breadcrumb.message = scrubUrl(breadcrumb.message);
    }
    return breadcrumb;
}

export function scrubEvent<T extends Event>(event: T): T {
    if (!event) {
        return event;
    }
    if (typeof event.transaction === 'string') {
        event.transaction = scrubUrl(event.transaction);
    }
    if (event.request) {
        if (typeof event.request.url === 'string') {
            event.request.url = scrubUrl(event.request.url);
        }
        if (event.request.query_string !== undefined) {
            event.request.query_string = scrubQueryString(event.request.query_string) as any;
        }
        const headers = event.request.headers as Record<string, string> | undefined;
        if (headers?.['Referer']) {
            headers['Referer'] = scrubUrl(headers['Referer']);
        }
    }
    event.breadcrumbs?.forEach(b => scrubBreadcrumb(b));
    (event as any).spans?.forEach((span: any) => {
        if (typeof span?.description === 'string') {
            span.description = scrubUrl(span.description);
        }
        scrubData(span?.data);
    });
    const trace = (event as any).contexts?.trace;
    if (trace) {
        scrubData(trace.data);
    }
    return event;
}
